import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { readCourseProgress, type CourseProgress } from '#shared/utils/course'

export function useCourseProgress(skillId: Ref<string>, lessonIds: Ref<string[]>) {
    const store = useApplicationStore()
    const progress = ref<CourseProgress>({ completedLessonIds: [], activeLessonId: null })
    const storageAvailable = ref(true)
    let mounted = false
    const storageKey = computed(
        () => `growni-course:v1:${encodeURIComponent(store.userId || 'guest')}:${encodeURIComponent(skillId.value)}`
    )

    function restore() {
        progress.value = readCourseProgress(null, lessonIds.value)
        try {
            progress.value = readCourseProgress(
                JSON.parse(localStorage.getItem(storageKey.value) || 'null'),
                lessonIds.value
            )
            storageAvailable.value = true
        } catch {
            storageAvailable.value = false
        }
    }

    function persist() {
        if (!mounted) return
        try {
            localStorage.setItem(storageKey.value, JSON.stringify(progress.value))
            storageAvailable.value = true
        } catch {
            storageAvailable.value = false
        }
    }

    onMounted(() => {
        mounted = true
        restore()
    })

    watch(storageKey, () => {
        if (mounted) restore()
    })
    watch(lessonIds, () => {
        progress.value = readCourseProgress(progress.value, lessonIds.value)
    })

    function openLesson(id: string) {
        if (!lessonIds.value.includes(id)) return
        progress.value.activeLessonId = id
        persist()
    }

    function toggleCompleted(id: string) {
        if (!lessonIds.value.includes(id)) return
        const completed = progress.value.completedLessonIds
        progress.value.completedLessonIds = completed.includes(id)
            ? completed.filter((item) => item !== id)
            : [...completed, id]
        persist()
    }

    return { progress, storageAvailable, openLesson, toggleCompleted }
}
